import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Stack, Tooltip, IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import PageHeader from '../components/PageHeader';
import PermissionGate from '../components/PermissionGate';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../api';

const emptyForm = { name: '', module: '', displayName: '', description: '' };

// Product-owned permission catalog. `module` groups permissions to match the entitlements
// product_permissions shape ({ "finance": ["invoice:read", ...] }) and the permission-sync payload.
const PermissionsManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/permissions');
      setRows(data.permissions);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to load permissions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (row) => {
    setEditing(row.id);
    setForm({ name: row.name, module: row.module, displayName: row.display_name, description: row.description || '' });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/permissions/${editing}`, {
          module: form.module, displayName: form.displayName, description: form.description
        });
        enqueueSnackbar('Permission updated', { variant: 'success' });
      } else {
        await api.post('/permissions', {
          name: form.name, module: form.module, displayName: form.displayName, description: form.description
        });
        enqueueSnackbar('Permission created', { variant: 'success' });
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/permissions/${deleteTarget.id}`);
      enqueueSnackbar('Permission deleted', { variant: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      field: 'name', headerName: 'Permission', width: 200,
      renderCell: (p) => <Chip label={p.value} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
    },
    {
      field: 'module', headerName: 'Module', width: 140,
      renderCell: (p) => <Chip label={p.value} size="small" color="primary" variant="outlined" />
    },
    { field: 'display_name', headerName: 'Display name', flex: 1, minWidth: 160 },
    { field: 'description', headerName: 'Description', flex: 1.2, minWidth: 200 },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction="row">
          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <PageHeader
        title="Permissions"
        subtitle="Product-owned fine-grained permissions, grouped by module."
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New permission</Button>
          </Stack>
        }
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      <Dialog open={formOpen} onClose={saving ? undefined : () => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit permission' : 'New permission'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name" fullWidth value={form.name} disabled={!!editing}
              helperText={editing ? 'Name is immutable' : 'e.g. invoice:read'}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Module" fullWidth value={form.module} placeholder="e.g. finance"
              onChange={(e) => setForm({ ...form, module: e.target.value })}
            />
            <TextField
              label="Display name" fullWidth value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
            <TextField
              label="Description" fullWidth multiline minRows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" onClick={save}
            disabled={saving || !form.name || !form.module || !form.displayName}
          >
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete permission?"
        message={`Delete "${deleteTarget?.name}"? This removes it from any roles that grant it.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

const Permissions = () => (
  <PermissionGate permission="users:manage">
    <PermissionsManager />
  </PermissionGate>
);

export default Permissions;
